import {
	Box,
	ButtonBase,
	CircularProgress,
	Skeleton,
	Stack,
	useTheme,
} from "@mui/material";
import { type FC, useState } from "react";
import { ChatAttachmentFile } from "web_myplann/Chat/Components/Attachments/ChatAttachmentFile";
import { ChatAttachmentViewer } from "web_myplann/Chat/Components/Attachments/ChatAttachmentViewer";
import { useChatAttachmentImage } from "web_myplann/Chat/Hooks/Attachments/useChatAttachmentImage";
import type {
	ChatAttachmentFileStyle,
	ChatAttachmentView,
} from "web_myplann/Chat/Types/chatMessage";

type Props = {
	attachment: ChatAttachmentView;
	fileStyle: ChatAttachmentFileStyle;
	isUploading?: boolean;
};

const MAX_HEIGHT = 240;
const SKELETON_SIZE = 180;

export const ChatAttachmentImage: FC<Props> = ({
	attachment,
	fileStyle,
	isUploading = false,
}) => {
	const theme = useTheme();
	const [isViewerOpen, setIsViewerOpen] = useState(false);

	const { id, fileName, mimeType, localUrl } = attachment;
	const isUploaded = id !== undefined;
	const { url: fetchedUrl, isError } = useChatAttachmentImage(
		id,
		isUploaded && localUrl === undefined,
	);
	const url = localUrl ?? fetchedUrl;

	if (isError) {
		return <ChatAttachmentFile attachment={attachment} fileStyle={fileStyle} />;
	}

	if (!url) {
		return (
			<Skeleton
				variant="rounded"
				width={SKELETON_SIZE}
				height={SKELETON_SIZE}
				sx={{ borderRadius: theme.radii.xl }}
			/>
		);
	}

	return (
		<>
			<ButtonBase
				disabled={!isUploaded}
				onClick={() => setIsViewerOpen(true)}
				sx={{
					display: "block",
					position: "relative",
					maxWidth: "100%",
					borderRadius: theme.radii.xl,
					overflow: "hidden",
				}}
			>
				<Box
					component="img"
					src={url}
					alt={fileName}
					draggable={false}
					display="block"
					maxWidth="100%"
					maxHeight={MAX_HEIGHT}
					sx={{ objectFit: "cover" }}
				/>

				{isUploading && (
					<Stack
						position="absolute"
						alignItems="center"
						justifyContent="center"
						sx={{ inset: 0 }}
					>
						<Stack
							alignItems="center"
							justifyContent="center"
							p={theme.spacingTokens.sm}
							bgcolor={theme.palette.background.paper}
							borderRadius={theme.radii.full}
						>
							<CircularProgress
								size={theme.iconSize.md}
								sx={{ color: theme.palette.blue[500] }}
							/>
						</Stack>
					</Stack>
				)}
			</ButtonBase>

			{isUploaded && (
				<ChatAttachmentViewer
					attachmentId={id}
					fileName={fileName}
					mimeType={mimeType}
					src={url}
					open={isViewerOpen}
					onClose={() => setIsViewerOpen(false)}
				/>
			)}
		</>
	);
};
